// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Pencil, Trash2, X, CheckCircle2, Clock, Heart, ClipboardList, Send,
  AlertTriangle, UserPlus, Eye, Copy, MessageSquare, Mail,
  Users, Smile, UserCheck, Smartphone} from 'lucide-react'
import { BackButton }      from '@/components/ui/BackButton'
import { InfoButton }      from '@/components/ui/InfoButton'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { Button, Input, Select } from '@/components/ui'
import { Card }            from '@/components/ui/Card'
import { Textarea }        from '@/components/ui/Textarea'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore }    from '@/store'
import { useToast }        from '@/hooks/useToast'
import { loadMessages, saveMessage, deleteMessage, type MessageDraft } from '@/services/wishes'
import { loadPartner, loadChildren } from '@/services/wizard'
import { loadTrustedPersons } from '@/services/trustedPerson'
import { DEMO_MODE }          from '@/config/demo'
import { ROUTES }             from '@/lib/constants/routes'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { truncate, formatDateNL } from '@/lib/utils'

// ── Constanten ────────────────────────────────────────────────────────────────


// N6: 7 dagen + eigen moment toegevoegd
const SEND_MOMENTS = [
  { value: 'direct',   label: 'Direct na activering' },
  { value: '24h',      label: '24 uur na overlijden' },
  { value: '48h',      label: '48 uur na overlijden' },
  { value: '7days',    label: '7 dagen na overlijden' },
  { value: 'funeral',  label: 'Na de uitvaart' },
  { value: 'custom',   label: 'Eigen moment' },
]

const WARM_TEMPLATES = [
  { recipientLabel: 'Mijn partner',    content: 'Lieve [naam],\n\nAls je dit bericht ontvangt ben ik overleden.\n\nIk wil je bedanken voor alle mooie jaren die we samen hebben gehad. Voor alle liefde, steun en herinneringen.\n\nIk hou van je.', status: 'READY' as const },
  { recipientLabel: 'Mijn kind(eren)', content: 'Lieve [naam],\n\nAls je dit leest ben ik er niet meer.\n\nOnthoud altijd dat ik ontzettend veel van je houd en trots op je ben. Koester de herinneringen die we samen hebben gemaakt.\n\nIk houd van je.', status: 'READY' as const },
  { recipientLabel: 'Familie',         content: 'Lieve familie,\n\nAls jullie dit lezen ben ik er niet meer.\n\nIk dank jullie voor alle mooie jaren en herinneringen. Zorg voor elkaar.\n\nMet veel liefde,\n[naam]', status: 'DRAFT' as const },
  { recipientLabel: 'Beste vriend(in)',content: 'Lieve [naam],\n\nIk wil je laten weten dat ik ben overleden.\n\nIk bedank je voor onze mooie vriendschap.\n\nAltijd dankbaar voor jou.', status: 'DRAFT' as const },
  { recipientLabel: 'Werkgever',       content: 'Geachte [naam werkgever / HR-afdeling],\n\nHierbij informeer ik u over het overlijden van [naam].\n\nDe nabestaande is te bereiken via [naam] op [telefoonnummer].\n\nMet vriendelijke groet.', status: 'DRAFT' as const },
  { recipientLabel: 'Bank',            content: 'Geachte [bank],\n\nHierbij meld ik u het overlijden van [naam], IBAN [rekeningnummer].\n\nIk verzoek u de rekening te verwerken conform uw procedure voor nabestaanden.\n\nMet vriendelijke groet,\n[naam nabestaande]', status: 'DRAFT' as const },
  { recipientLabel: 'Verzekeraar',     content: 'Geachte [verzekeraar],\n\nHierbij meld ik het overlijden van [naam], polisnummer [nummer].\n\nIk verzoek u mij te informeren over de verdere afhandeling.\n\nMet vriendelijke groet,\n[naam nabestaande]', status: 'DRAFT' as const },
  { recipientLabel: 'Pensioenfonds',   content: 'Geachte [pensioenfonds],\n\nHierbij meld ik het overlijden van [naam], deelnemersnummer [nummer].\n\nIk verzoek u mij te informeren over het nabestaandenpensioen.\n\nMet vriendelijke groet,\n[naam nabestaande]', status: 'DRAFT' as const },
]


// ── Berichtgroepering (V48) ──────────────────────────────────────────────────
// Detectie op recipientLabel - geen schemawijziging, backward compat
const PRAKTISCHE_LABELS = ['werkgever', 'bank', 'verzekeraar', 'pensioenfonds', 'zorgverzekering', 'belasting', 'gemeente', 'notaris', 'uitvaart']
function isPraktisch(label: string): boolean {
  const l = label.toLowerCase()
  return PRAKTISCHE_LABELS.some(p => l.includes(p))
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface MsgForm {
  recipientLabel: string; recipientPhone: string; recipientEmail: string
  relation: string; sendMethod: string; sendMoment: string
  customMoment: string
  content: string; status: 'DRAFT'|'READY'
}
const EMPTY_FORM: MsgForm = {
  recipientLabel: '', recipientPhone: '', recipientEmail: '',
  relation: '', sendMethod: 'whatsapp', sendMoment: 'direct',
  customMoment: '', content: '', status: 'DRAFT',
}

// M3: bekende contacten
interface KnownContact {
  group:     'partner' | 'children' | 'vp' | 'phone' | 'new'
  init:      string
  initColor: string
  label:     string
  name:      string
  relation:  string
  phone:     string
  email:     string
}

interface ContactGroup {
  id:       KnownContact['group']
  label:    string
  icon:     React.ReactNode
  count:    number
  contacts: KnownContact[]
}

// ── Sub-componenten ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  if (status === 'READY') return (
    <span className="inline-flex items-center gap-1 rounded-full bg-sage-50 border border-sage-200 px-2 py-0.5 text-xs font-medium text-sage-700">
      <CheckCircle2 className="h-3 w-3" /> Klaar
    </span>
  )
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-medium text-amber-700">
      <Clock className="h-3 w-3" /> Concept
    </span>
  )
}

// N7: Preview modal
function MessagePreviewModal({
  message, method, onClose,
}: {
  message: MessageDraft
  method: string
  onClose: () => void
}) {
  const text = message.content || '(geen inhoud)'
  // V49-RC: de kanaaltabs wisselen nu het voorbeeld (lokaal), zodat ze niet "dood" aanvoelen.
  // Het werkelijke verzendkanaal (message.sendMethod) verandert hierdoor niet.
  const [previewCh, setPreviewCh] = useState<'whatsapp' | 'sms' | 'email'>(
    method === 'sms' ? 'sms' : method === 'email' ? 'email' : 'whatsapp',
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-sand-200">
          <p className="text-sm font-semibold text-ink">Voorbeeld - {message.recipientLabel}</p>
          <button onClick={onClose} aria-label="Sluiten" className="flex h-8 w-8 items-center justify-center rounded-full text-ink-4 hover:bg-sand-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Kanaaltabs */}
        <div className="flex border-b border-sand-200" role="tablist" aria-label="Voorbeeldkanaal">
          {(['whatsapp','sms','email'] as const).map(ch => (
            <button key={ch} type="button" role="tab" aria-selected={previewCh === ch} onClick={() => setPreviewCh(ch)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
                previewCh === ch
                  ? 'border-b-2 border-sage-600 text-sage-700 bg-sage-50'
                  : 'text-ink-4 hover:text-ink-3'
              }`}>
              {ch === 'whatsapp' ? 'WhatsApp' : ch === 'sms' ? 'SMS' : 'E-mail'}
            </button>
          ))}
        </div>

        {/* Preview content */}
        <div className="p-5">
          {previewCh === 'whatsapp' && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-ink-4 mb-1">Zo ziet het eruit in WhatsApp:</p>
              <div className="flex justify-end">
                <div className="max-w-[80%] bg-[#DCF8C6] rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
                  <p className="text-sm text-[#111] whitespace-pre-wrap break-words leading-relaxed">{text}</p>
                  <p className="text-[10px] text-[#999] text-right mt-1">via WilsApp ✓✓</p>
                </div>
              </div>
            </div>
          )}
          {previewCh === 'sms' && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-ink-4 mb-1">Zo ziet het eruit als SMS:</p>
              <div className="bg-[#E9E9EB] rounded-2xl rounded-tl-sm px-4 py-3">
                <p className="text-sm text-[#111] whitespace-pre-wrap break-words leading-relaxed">{text}</p>
              </div>
            </div>
          )}
          {previewCh === 'email' && (
            <div className="flex flex-col gap-2 border border-sand-300 rounded-xl p-4">
              <div className="border-b border-sand-200 pb-3 flex flex-col gap-1">
                <p className="text-xs text-ink-4"><span className="font-semibold">Aan:</span> {message.recipientLabel}</p>
                <p className="text-xs text-ink-4"><span className="font-semibold">Onderwerp:</span> Persoonlijk bericht</p>
              </div>
              <p className="text-sm text-ink whitespace-pre-wrap break-words leading-relaxed pt-1">{text}</p>
            </div>
          )}
        </div>

        {/* Demo-disclaimer */}
        <div className="px-5 pb-5">
          <div className="rounded-xl bg-status-warnBg border border-status-warnBorder px-3 py-2">
            <p className="text-xs text-status-warnText text-center">
              Pilotversie - berichten worden bewaard maar nog niet verstuurd
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}


// ── Verzendknoppen (MVP deeplinks) ──────────────────────────────────────────────

function encodeMsg(text: string) {
  return encodeURIComponent(text)
}

function cleanPhone(raw: string): string {
  // Strip spaces, dashes, parentheses; convert 06 → +316 for wa.me
  const digits = raw.replace(/[^0-9+]/g, '')
  if (digits.startsWith('06'))  return '+316' + digits.slice(2)
  if (digits.startsWith('00'))  return '+' + digits.slice(2)
  return digits
}

interface SendActionsProps {
  message: MessageDraft
}

function SendActions({ message }: SendActionsProps) {
  const phone   = (message.recipientPhone ?? '').trim()
  const email   = (message.recipientEmail ?? '').trim()
  const content = message.content ?? ''
  const name    = message.recipientLabel ?? 'WilsApp'
  const [copied, setCopied] = useState(false)

  const hasPhone = !!phone
  const hasEmail = !!email

  function openWhatsApp() {
    const tel = cleanPhone(phone)
    const url = `https://wa.me/${tel}?text=${encodeMsg(content)}`
    window.open(url, '_blank', 'noopener')
  }

  function openSMS() {
    const tel = cleanPhone(phone)
    // iOS: sms:+316...&body=..., Android: sms:+316...?body=...
    // Use & for broadest compat; most mobile browsers handle both
    const url = `sms:${tel}?body=${encodeMsg(content)}`
    window.location.href = url
  }

  function openEmail() {
    const subject = encodeMsg(`Bericht van ${name}`)
    const body    = encodeMsg(content)
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`
  }

  async function copyText() {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select text
      const el = document.createElement('textarea')
      el.value = content
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      {/* Demo-melding */}
      <p className="text-xs text-ink-4 leading-relaxed bg-sand rounded-lg px-3 py-2">
        WilsApp opent uw gekozen app. U controleert en verzendt het bericht zelf.
      </p>

      {/* Knoppen */}
      <div className="flex flex-wrap gap-2">
        {/* WhatsApp */}
        <button
          type="button"
          onClick={hasPhone ? openWhatsApp : undefined}
          disabled={!hasPhone}
          title={hasPhone ? `WhatsApp naar ${phone}` : 'Voeg een telefoonnummer toe aan dit bericht'}
          className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-medium transition-all active:scale-[0.97] ${
            hasPhone
              ? 'bg-[#25D366] text-white hover:bg-[#1ebe5d] shadow-sm'
              : 'bg-sand-200 text-ink-4 cursor-not-allowed'
          }`}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          WhatsApp
          {!hasPhone && <span className="text-[10px] ml-0.5 opacity-70">- geen nummer</span>}
        </button>

        {/* SMS */}
        <button
          type="button"
          onClick={hasPhone ? openSMS : undefined}
          disabled={!hasPhone}
          title={hasPhone ? `SMS naar ${phone}` : 'Voeg een telefoonnummer toe aan dit bericht'}
          className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-medium transition-all active:scale-[0.97] ${
            hasPhone
              ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
              : 'bg-sand-200 text-ink-4 cursor-not-allowed'
          }`}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          SMS
          {!hasPhone && <span className="text-[10px] ml-0.5 opacity-70">- geen nummer</span>}
        </button>

        {/* E-mail */}
        <button
          type="button"
          onClick={hasEmail ? openEmail : undefined}
          disabled={!hasEmail}
          title={hasEmail ? `E-mail naar ${email}` : 'Voeg een e-mailadres toe aan dit bericht'}
          className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-medium transition-all active:scale-[0.97] ${
            hasEmail
              ? 'bg-sky-500 text-white hover:bg-sky-600 shadow-sm'
              : 'bg-sand-200 text-ink-4 cursor-not-allowed'
          }`}
        >
          <Mail className="h-3.5 w-3.5" />
          E-mail
          {!hasEmail && <span className="text-[10px] ml-0.5 opacity-70">- geen adres</span>}
        </button>

        {/* Kopieer */}
        <button
          type="button"
          onClick={copyText}
          className="flex items-center gap-1.5 rounded-lg border border-sand-300 bg-white px-3.5 py-2 text-xs font-medium text-ink-3 hover:border-sage-300 hover:text-sage-700 hover:bg-sage-50/50 transition-all active:scale-[0.97]"
        >
          {copied
            ? <><CheckCircle2 className="h-3.5 w-3.5 text-sage-600" /><span className="text-sage-600">Gekopieerd</span></>
            : <><Copy className="h-3.5 w-3.5" />Kopieer</>
          }
        </button>
      </div>
    </div>
  )
}

const DEMO_PHONE_CONTACTS: KnownContact[] = [
  { group: 'phone', init: 'AJ', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Anja Janssen',     name: 'Anja Janssen',     relation: 'Anders', phone: '06 11223344', email: 'anja@janssen.nl' },
  { group: 'phone', init: 'BH', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Bert Hendriks',    name: 'Bert Hendriks',    relation: 'Anders', phone: '06 22334455', email: '' },
  { group: 'phone', init: 'CW', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Corrie de Wit',    name: 'Corrie de Wit',    relation: 'Anders', phone: '06 33445566', email: 'corrie@dewit.nl' },
  { group: 'phone', init: 'DM', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Dick Mulder',      name: 'Dick Mulder',      relation: 'Anders', phone: '06 44556677', email: 'd.mulder@mail.nl' },
  { group: 'phone', init: 'EV', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Els Visser',       name: 'Els Visser',       relation: 'Anders', phone: '06 55667788', email: 'els@visser.nl' },
  { group: 'phone', init: 'FP', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Frank Peters',     name: 'Frank Peters',     relation: 'Anders', phone: '06 66778899', email: '' },
  { group: 'phone', init: 'GS', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Greet Smits',      name: 'Greet Smits',      relation: 'Anders', phone: '06 77889900', email: 'greet@smits.nl' },
  { group: 'phone', init: 'HN', initColor: 'bg-blue-100 text-blue-700', label: 'Telefoon - Hans Nieuwenhuis', name: 'Hans Nieuwenhuis', relation: 'Anders', phone: '06 88990011', email: 'hans@mail.nl' },
]

function StapLabel({ nr, text }: { nr: number; text: string }) {
  return (
    <p className="text-xs font-semibold text-sage-700 flex items-center gap-1.5">
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sage-600 text-white text-[10px] font-bold flex-shrink-0">{nr}</span>
      {text}
    </p>
  )
}

function ContactPicker({
  groups, selectedContact, onSelect,
}: {
  groups:          ContactGroup[]
  selectedContact: KnownContact | null
  onSelect:        (c: KnownContact) => void
}) {
  const [activeGroup, setActiveGroup] = React.useState<KnownContact['group'] | null>(null)

  function handleTileClick(g: ContactGroup) {
    if (g.id === 'new') {
      onSelect({ group: 'new', init: '+', initColor: 'bg-sand-200 text-ink-4', label: 'Nieuwe ontvanger', name: '', relation: '', phone: '', email: '' })
      setActiveGroup(null)
      return
    }
    if (g.contacts.length === 1) { onSelect(g.contacts[0]); setActiveGroup(null); return }
    setActiveGroup(prev => prev === g.id ? null : g.id)
  }

  const openGroup = groups.find(g => g.id === activeGroup)

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {groups.map(g => {
          const isActive   = activeGroup === g.id
          const isSelected = selectedContact?.group === g.id && !isActive
          return (
            <button key={g.id} type="button" onClick={() => handleTileClick(g)}
              className={`flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-center transition-all min-w-0
                ${g.id === 'new' ? 'border-dashed' : ''}
                ${isActive    ? 'border-sage-600 bg-sage-50' :
                  isSelected  ? 'border-sage-400 bg-sage-50/60' :
                                'border-sand-200 bg-white hover:border-sage-300 hover:bg-sage-50/40'}`}>
              <div className={`flex h-9 w-9 items-center justify-center rounded-full
                ${g.id === 'partner'  ? 'bg-violet-100 text-violet-600' :
                  g.id === 'children' ? 'bg-amber-100 text-amber-600' :
                  g.id === 'vp'       ? 'bg-sage-100 text-sage-600' :
                  g.id === 'phone'    ? 'bg-blue-100 text-blue-600' :
                                        'bg-sand-200 text-ink-4'}`}>
                {g.icon}
              </div>
              <span className="text-[11px] font-medium text-ink leading-tight w-full break-words">{g.label}</span>
              {g.id !== 'new' && <span className="text-[10px] text-ink-4">{g.count} {g.count === 1 ? 'persoon' : 'personen'}</span>}
            </button>
          )
        })}
      </div>

      {openGroup && (
        <div className="rounded-xl border border-sage-200 bg-sage-50/50 p-3 animate-slide-down">
          <p className="text-xs font-semibold text-sage-700 mb-2 flex items-center gap-1.5">
            {openGroup.icon}
            {openGroup.id === 'phone'
              ? <span>Demo-telefoonboek <span className="text-[10px] font-normal text-sage-600 ml-1">(fictieve namen)</span></span>
              : <span>Kies een persoon uit {openGroup.label.toLowerCase()}</span>}
          </p>
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
            {openGroup.contacts.map((c, i) => (
              <button key={i} type="button" onClick={() => { onSelect(c); setActiveGroup(null) }}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-all
                  ${selectedContact?.name === c.name
                    ? 'bg-sage-100 border border-sage-300'
                    : 'bg-white border border-sand-200 hover:border-sage-300 hover:bg-sage-50/50'}`}>
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 ${c.initColor}`}>
                  {c.init}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{c.name}</p>
                  <p className="text-xs text-ink-4 truncate">{c.phone || c.email || 'Geen contactgegevens'}</p>
                </div>
                {selectedContact?.name === c.name && <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Hoofdcomponent ─────────────────────────────────────────────────────────────

export function MessagesPage() {
  const navigate = useNavigate()
  const { userId } = useAuthStore()
  const { toast }  = useToast()

  const [messages,      setMessages]      = useState<MessageDraft[]>([])
  const [isLoading,     setIsLoading]     = useState(true)
  const [showForm,      setShowForm]      = useState(false)
  const [editId,        setEditId]        = useState<string | null>(null)
  const [form,          setForm]          = useState<MsgForm>(EMPTY_FORM)
  const [isSaving,      setIsSaving]      = useState(false)
  const [demoLoaded,    setDemoLoaded]    = useState(false)
  // M3: bekende contacten (tegel-UX)
  const [contactGroups,     setContactGroups]     = useState<ContactGroup[]>([])
  const [selectedContact,   setSelectedContact]   = useState<KnownContact | null>(null)
  const [showChildProposal, setShowChildProposal] = useState(false)
  // N7: preview
  const [previewMsg,    setPreviewMsg]    = useState<MessageDraft | null>(null)
  // V48-P2: Bulk verzenden
  const [bulkPhase, setBulkPhase] = useState<'idle'|'confirm'|'sending'>('idle')
  const [bulkIndex, setBulkIndex] = useState(0)
  const [bulkQueue, setBulkQueue] = useState<MessageDraft[]>([])
  const [previewMethod, setPreviewMethod] = useState('whatsapp')

  // Laad bekende contacten als groepen voor tegel-UX
  useEffect(() => {
    if (!userId) return
    async function loadContacts() {
      const groups: ContactGroup[] = []

      const partner = await loadPartner(userId!)
      const partnerList: KnownContact[] = []
      if (partner?.hasPartner === 'yes' && partner.name?.trim()) {
        partnerList.push({ group: 'partner', init: partner.name.slice(0,2).toUpperCase(),
          initColor: 'bg-violet-100 text-violet-600', label: `Partner - ${partner.name}`,
          name: partner.name, relation: 'Partner', phone: partner.phone ?? '', email: partner.email ?? '' })
      }
      if (partnerList.length > 0)
        groups.push({ id: 'partner', label: 'Partner', icon: <Users className="h-4 w-4" />, count: partnerList.length, contacts: partnerList })

      const children = await loadChildren(userId!)
      const childList: KnownContact[] = []
      if (children?.hasChildren === 'yes') {
        children.children.filter(c => c.name?.trim()).forEach(c => {
          childList.push({ group: 'children', init: c.name.slice(0,2).toUpperCase(),
            initColor: 'bg-amber-100 text-amber-600', label: `Kind - ${c.name}`,
            name: c.name, relation: 'Kind', phone: '', email: '' })
        })
        const existingMsgs = await loadMessages(userId!)
        const hasKidMsg = existingMsgs.some(m =>
          children.children.some(ch => ch.name && m.recipientLabel.toLowerCase().includes(ch.name.toLowerCase()))
        )
        if (!hasKidMsg && children.children.length > 0) setShowChildProposal(true)
      }
      if (childList.length > 0)
        groups.push({ id: 'children', label: 'Kinderen', icon: <Smile className="h-4 w-4" />, count: childList.length, contacts: childList })

      const vps = await loadTrustedPersons(userId!)
      const vpList: KnownContact[] = vps.filter(v => v.confirmationStatus !== 'REVOKED').map(v => ({
        group: 'vp' as const, init: v.firstName.slice(0,2).toUpperCase(),
        initColor: 'bg-sage-100 text-sage-600', label: `Vertrouwenspersoon - ${v.firstName}`,
        name: v.firstName, relation: 'Familie', phone: '', email: v.email ?? ''
      }))
      if (vpList.length > 0)
        groups.push({ id: 'vp', label: 'Vertrouwenspersonen', icon: <UserCheck className="h-4 w-4" />, count: vpList.length, contacts: vpList })

      if (DEMO_MODE) {
        groups.push({ id: 'phone', label: 'Telefoon', icon: <Smartphone className="h-4 w-4" />,
          count: DEMO_PHONE_CONTACTS.length, contacts: DEMO_PHONE_CONTACTS })
      }

      groups.push({ id: 'new', label: 'Nieuw', icon: <UserPlus className="h-4 w-4" />, count: 0, contacts: [] })
      setContactGroups(groups)
    }
    loadContacts()
  }, [userId])

  function startBulkSend() {
    const ready = messages.filter(m => m.status === 'READY')
    if (ready.length === 0) return
    setBulkQueue(ready)
    setBulkIndex(0)
    setBulkPhase('confirm')
  }

  function bulkOpenCurrent() {
    const m = bulkQueue[bulkIndex]
    if (!m) return
    const phone   = (m.recipientPhone ?? '').trim()
    const email   = (m.recipientEmail ?? '').trim()
    const content = m.content ?? ''
    const name    = m.recipientLabel ?? 'WilsApp'
    const method  = m.sendMethod ?? 'whatsapp'
    const tel     = cleanPhone(phone)
    if (method === 'whatsapp' && phone) {
      window.open(`https://wa.me/${tel}?text=${encodeMsg(content)}`, '_blank', 'noopener')
    } else if (method === 'sms' && phone) {
      window.location.href = `sms:${tel}?body=${encodeMsg(content)}`
    } else if (method === 'email' && email) {
      const sub  = encodeMsg(`Bericht van ${name}`)
      const body = encodeMsg(content)
      window.location.href = `mailto:${email}?subject=${sub}&body=${body}`
    }
  }

  function bulkNext() {
    if (bulkIndex < bulkQueue.length - 1) {
      setBulkIndex(i => i + 1)
    } else {
      setBulkPhase('idle')
    }
  }

  async function reload() {
    if (!userId) return [] as MessageDraft[]
    const msgs = await loadMessages(userId)
    setMessages(msgs)
    return msgs
  }

  useEffect(() => {
    if (!userId) return
    reload().then(async msgs => {
      if (!msgs || msgs.length === 0) {
        // Sla alleen templates op die nog niet bestaan (check op recipientLabel)
        for (const dm of WARM_TEMPLATES) {
          const exists = msgs.some(m => m.recipientLabel === dm.recipientLabel)
          if (!exists) await saveMessage(userId, dm)
        }
        await reload()
        setDemoLoaded(true)
      }
      setIsLoading(false)
    })
  }, [userId])

  function openNew() { setForm(EMPTY_FORM); setEditId(null); setSelectedContact(null); setShowForm(true) }
  function openEdit(m: MessageDraft) {
    setForm({
      recipientLabel: m.recipientLabel,
      recipientPhone: m.recipientPhone ?? '',
      recipientEmail: m.recipientEmail ?? '',
      relation:       m.relation ?? '',
      sendMethod:     m.sendMethod ?? 'whatsapp',
      sendMoment:     m.sendMoment ?? 'direct',
      customMoment:   m.customMoment ?? '',
      content:        m.content,
      status:         m.status as 'DRAFT'|'READY',
    })
    setEditId(m.id); setSelectedContact(null); setShowForm(true)
  }

  function handleContactSelect(c: KnownContact) {
    setSelectedContact(c)
    setForm(prev => ({
      ...prev,
      recipientLabel: c.name,
      relation:       c.relation,
      recipientPhone: c.phone,
      recipientEmail: c.email,
    }))
  }

  async function handleSave(ready = false) {
    if (!userId || !form.recipientLabel.trim() || !form.content.trim()) return
    setIsSaving(true)
    try {
      await saveMessage(
        userId,
        {
          recipientLabel: form.recipientLabel,
          content:        form.content,
          status:         ready ? 'READY' : 'DRAFT',
          recipientPhone: form.recipientPhone,
          recipientEmail: form.recipientEmail,
          relation:       form.relation,
          sendMethod:     form.sendMethod,
          sendMoment:     form.sendMoment,
          customMoment:   form.customMoment,
        },
        editId ?? undefined,
      )
      toast.success(editId ? 'Bericht bijgewerkt' : 'Bericht toegevoegd')
      setShowForm(false); setEditId(null); setSelectedContact(null)
      await reload()
    } catch { toast.error('Opslaan mislukt') }
    finally { setIsSaving(false) }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Verwijderen?')) return
    await deleteMessage(id); await reload()
  }

  function openPreview(m: MessageDraft) {
    setPreviewMsg(m)
    setPreviewMethod('whatsapp')
  }


  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div>
      {/* N7: Preview modal */}
      {previewMsg && (
        <MessagePreviewModal
          message={previewMsg}
          method={previewMethod}
          onClose={() => setPreviewMsg(null)}
        />
      )}

      <BackButton />
      <ModuleShell
        title="Berichtenservice"
        description="Persoonlijke berichten voor uw naasten. Stel ze rustig samen nu u uw woorden zelf kunt kiezen."
        actions={
          <div className="flex items-center gap-2">
            <InfoButton title="Berichtenservice"
              content={<>
                <p>Schrijf nu berichten die uw naasten ontvangen na uw overlijden.</p>
                <p><strong>Persoonlijke berichten</strong> aan uw partner of kinderen zijn het meest waardevol.</p>
                <p><strong>Zakelijke berichten</strong> helpen uw naasten bij het informeren van werkgever, bank en verzekeraar.</p>
                <p>Berichten worden opgeslagen. Versturen is beschikbaar in de definitieve versie.</p>
              </>}
            />
            <Button size="sm" onClick={openNew} icon={<Plus className="h-4 w-4" />}>Nieuw</Button>
          </div>
        }
      >
        {/* V49: intro Berichtenservice */}
        <div className="rounded-2xl bg-sage-50 border border-sage-100 px-5 py-4">
          <p className="text-sm text-ink-2 leading-relaxed">
            Wilt u dat een persoonlijk bericht op een later moment met iemand wordt gedeeld? Met de
            Berichtenservice bepaalt u voor wie een bericht bedoeld is, hoe dit later mag worden gedeeld
            en wanneer uw vertrouwenspersoon of bevoegde nabestaande dit bericht beschikbaar mag maken.
          </p>
          <p className="text-sm text-ink-3 leading-relaxed mt-2">
            In deze versie verstuurt WilsApp nooit automatisch berichten. Uw vertrouwenspersoon houdt
            altijd de regie over het delen van uw boodschappen volgens uw vastgelegde wensen.
          </p>
        </div>

        {/* Demo-banner */}
        <div className="flex items-start gap-3 rounded-xl border border-status-warnBorder bg-status-warnBg px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-status-warnText flex-shrink-0 mt-0.5" />
          <p className="text-xs text-status-warnText">
            <strong>Let op:</strong> WilsApp verstuurt berichten niet automatisch. U of uw vertrouwenspersoon
            opent elk bericht via WhatsApp, SMS of e-mail en verstuurt het zelf op het juiste moment.
          </p>
        </div>

        {demoLoaded && (
          <div className="flex items-center gap-2 rounded-xl bg-sage-50 border border-sage-200 px-4 py-2.5">
            <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0" />
            <p className="text-xs text-sage-700">Voorbeeldberichten geladen. Klik op een bericht om het te bewerken.</p>
          </div>
        )}

        {/* Punt 23: voorstel voor kinderberichten */}
        {showChildProposal && (
          <div className="flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-200 px-4 py-3">
            <span className="text-base flex-shrink-0">💌</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-900 mb-1">
                U heeft kinderen geregistreerd. Wilt u een persoonlijk bericht achterlaten?
              </p>
              <p className="text-xs text-blue-700 mb-3">
                Een persoonlijk bericht aan uw kinderen is één van de meest gewaardeerde onderdelen van WilsApp.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { openNew(); setShowChildProposal(false) }}
                  className="rounded-lg bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 transition-colors"
                >
                  Ja, bericht schrijven
                </button>
                <button
                  onClick={() => setShowChildProposal(false)}
                  className="rounded-lg border border-blue-300 text-blue-700 text-xs font-semibold px-3 py-1.5 hover:bg-blue-100 transition-colors"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Formulier */}
        {showForm && (
          <Card className="border-sage-200">
            {/* Formulier-header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-moss">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <p className="text-sm font-semibold text-ink">{editId ? 'Bericht bewerken' : 'Nieuw bericht'}</p>
              </div>
              <button onClick={() => { setShowForm(false); setSelectedContact(null) }}
                className="flex h-8 w-8 items-center justify-center rounded-full text-ink-4 hover:bg-sand-100">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col gap-5">
              {/* Stap 1 - Contact */}
              {!editId && contactGroups.length > 0 && (
                <div className="flex flex-col gap-2">
                  <StapLabel nr={1} text="Selecteer een contact uit WilsApp" />
                  <ContactPicker groups={contactGroups} selectedContact={selectedContact} onSelect={handleContactSelect} />
                </div>
              )}

              {/* Stap 2 + 3 - twee kolommen */}
              <div className="grid gap-4 sm:grid-cols-2">

                {/* Links: ontvanger gegevens */}
                <div className="flex flex-col gap-3">
                  <StapLabel nr={editId ? 1 : 2} text="Ontvanger gegevens" />

                  {selectedContact?.name && (
                    <div className="flex items-center gap-2 rounded-xl bg-sage-50 border border-sage-200 px-3 py-2.5">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 ${selectedContact.initColor}`}>
                        {selectedContact.init}
                      </div>
                      <span className="text-sm font-medium text-sage-800 flex-1 truncate">Geselecteerd: {selectedContact.name}</span>
                      <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0" />
                    </div>
                  )}

                  <Input label="Naam ontvanger" value={form.recipientLabel}
                    onChange={e => setForm(p => ({...p, recipientLabel: e.target.value}))}
                    placeholder="Bijv: Mijn dochter Emma" required />

                  <Input label="Mobiel nummer (optioneel)" type="tel" value={form.recipientPhone}
                    onChange={e => setForm(p => ({...p, recipientPhone: e.target.value}))}
                    placeholder="06-..."
                    hint="Voor WhatsApp of SMS" />

                  <Input label="E-mail (optioneel)" type="email" value={form.recipientEmail}
                    onChange={e => setForm(p => ({...p, recipientEmail: e.target.value}))}
                    placeholder="naam@mail.nl" />

                  {selectedContact?.name && (
                    <p className="text-xs text-ink-4 flex items-center gap-1">
                      <span className="text-blue-400 text-sm">ⓘ</span>
                      Automatisch ingevuld - pas aan indien nodig.
                    </p>
                  )}
                </div>

                {/* Rechts: verzendkanaal */}
                <div className="flex flex-col gap-3">
                  <StapLabel nr={editId ? 2 : 3} text="Bericht verzenden via" />

                  {/* WhatsApp kaart */}
                  <button type="button" onClick={() => setForm(p => ({...p, sendMethod: 'whatsapp'}))}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      form.sendMethod === 'whatsapp' || form.sendMethod === 'all'
                        ? 'border-sage-600 bg-sage-50' : 'border-sand-200 bg-white hover:border-sage-300'}`}>
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#25D366] flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-ink flex items-center gap-2">
                        WhatsApp
                        {(form.sendMethod === 'whatsapp' || form.sendMethod === 'all') &&
                          <span className="text-[10px] font-medium bg-sage-100 text-sage-700 px-2 py-0.5 rounded-full">Aanbevolen</span>}
                      </p>
                      <p className="text-xs text-ink-4">Snel en persoonlijk</p>
                    </div>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      form.sendMethod === 'whatsapp' || form.sendMethod === 'all'
                        ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                  </button>

                  {/* E-mail kaart */}
                  <button type="button" onClick={() => setForm(p => ({...p, sendMethod: 'email'}))}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      form.sendMethod === 'email'
                        ? 'border-sage-600 bg-sage-50' : 'border-sand-200 bg-white hover:border-sage-300'}`}>
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-500 flex-shrink-0">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-ink">E-mail</p>
                      <p className="text-xs text-ink-4">Veilig en betrouwbaar</p>
                    </div>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      form.sendMethod === 'email' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                  </button>

                  {/* SMS kaart */}
                  <button type="button" onClick={() => setForm(p => ({...p, sendMethod: 'sms'}))}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      form.sendMethod === 'sms'
                        ? 'border-sage-600 bg-sage-50' : 'border-sand-200 bg-white hover:border-sage-300'}`}>
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500 flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-ink">SMS</p>
                      <p className="text-xs text-ink-4">Altijd bezorgd</p>
                    </div>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      form.sendMethod === 'sms' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                  </button>

                  {/* Wanneer select */}
                  <Select label="Wanneer" options={SEND_MOMENTS} value={form.sendMoment}
                    onChange={e => setForm(p => ({...p, sendMoment: e.target.value}))} />
                  {form.sendMoment === 'custom' && (
                    <Input label="Gewenst moment" value={form.customMoment}
                      onChange={e => setForm(p => ({...p, customMoment: e.target.value}))}
                      placeholder="Bijv: 3 maanden na overlijden..." />
                  )}
                </div>
              </div>

              {/* Berichtinhoud */}
              <Textarea label="Uw bericht" value={form.content}
                onChange={e => setForm(p => ({...p, content: e.target.value}))}
                placeholder="Schrijf hier uw persoonlijke bericht..." maxLength={3000} showCount
                className="min-h-[160px]" />

              {/* Gele tip */}
              <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                <span className="text-base flex-shrink-0">💡</span>
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong>Goed om te weten:</strong> Uw vertrouwenspersoon ontvangt een melding
                  en beslist wanneer dit bericht wordt verzonden.
                </p>
              </div>

              <div className="flex gap-3">
                <Button fullWidth onClick={() => handleSave(false)} loading={isSaving} variant="secondary">
                  Concept opslaan
                </Button>
                <Button fullWidth onClick={() => handleSave(true)} loading={isSaving}
                  icon={<CheckCircle2 className="h-4 w-4" />}>
                  Klaar
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* V48-P2: Bevestigingsmodal - Alles verzenden */}
        {bulkPhase === 'confirm' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={() => setBulkPhase('idle')}>
            <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}>
              <div className="px-6 py-5 flex flex-col gap-4">
                <div>
                  <p className="text-base font-semibold text-ink mb-2">Alle berichten verzenden</p>
                  <p className="text-sm text-ink-3 leading-relaxed">
                    U staat op het punt alle {bulkQueue.length} klaargezette berichten één voor één
                    te openen in de gekozen apps. Controleer elk bericht voordat u het verzendt.
                  </p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                  <p className="text-xs text-amber-700 leading-relaxed">
                    WilsApp verstuurt nooit automatisch. U controleert en verzendt elk bericht zelf.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setBulkPhase('idle')}
                    className="flex-1 rounded-xl border-2 border-sand-300 px-4 py-3 text-sm font-medium text-ink-3 hover:border-sage-300 transition-colors">
                    Annuleren
                  </button>
                  <button onClick={() => setBulkPhase('sending')}
                    className="flex-1 rounded-xl bg-moss text-white px-4 py-3 text-sm font-semibold hover:bg-moss-light transition-colors">
                    Starten
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* V48-P2: Bulk verzend-flow */}
        {bulkPhase === 'sending' && bulkQueue[bulkIndex] && (() => {
          const m        = bulkQueue[bulkIndex]
          const isLast   = bulkIndex === bulkQueue.length - 1
          const phone    = (m.recipientPhone ?? '').trim()
          const email    = (m.recipientEmail ?? '').trim()
          const method   = m.sendMethod ?? 'whatsapp'
          const canSend  = (method === 'email' && !!email) || (method !== 'email' && !!phone)
          return (
            <div className="fixed inset-0 z-50 flex flex-col bg-sand overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-sand-200 flex-shrink-0">
                <div>
                  <p className="text-xs text-ink-4 font-medium">
                    Bericht {bulkIndex + 1} van {bulkQueue.length}
                  </p>
                  <p className="text-sm font-semibold text-ink">{m.recipientLabel}</p>
                </div>
                <button onClick={() => setBulkPhase('idle')}
                  className="flex items-center gap-1.5 text-xs text-ink-4 hover:text-ink-3 transition-colors">
                  <X className="h-4 w-4" />
                  Stoppen
                </button>
              </div>

              {/* Voortgangsbalk */}
              <div className="h-1 bg-sand-200 flex-shrink-0">
                <div className="h-1 bg-sage-500 transition-all duration-300"
                  style={{ width: `${((bulkIndex + 1) / bulkQueue.length) * 100}%` }} />
              </div>

              {/* Berichtinhoud */}
              <div className="flex-1 px-5 py-5 flex flex-col gap-4 max-w-2xl mx-auto w-full">
                <div className="rounded-2xl bg-white border border-sand-200 px-5 py-4">
                  <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Aan</p>
                  <p className="text-sm font-semibold text-ink mb-1">{m.recipientLabel}</p>
                  {phone && <p className="text-xs text-ink-4">{phone}</p>}
                  {email && <p className="text-xs text-ink-4">{email}</p>}
                </div>

                <div className="rounded-2xl bg-white border border-sand-200 px-5 py-4">
                  <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Bericht</p>
                  <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap break-words">{m.content}</p>
                </div>

                {/* Verzendknop */}
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-ink-4 text-center">
                    Via: {method === 'whatsapp' ? 'WhatsApp' : method === 'sms' ? 'SMS' : 'E-mail'}
                  </p>
                  <button onClick={bulkOpenCurrent} disabled={!canSend}
                    className={`flex items-center justify-center gap-2 w-full rounded-2xl px-5 py-4 text-sm font-semibold transition-colors ${
                      canSend
                        ? method === 'whatsapp' ? 'bg-[#25D366] text-white hover:bg-[#1ebe5d]'
                        : method === 'sms'       ? 'bg-blue-500 text-white hover:bg-blue-600'
                        :                          'bg-sky-500 text-white hover:bg-sky-600'
                        : 'bg-sand-200 text-ink-4 cursor-not-allowed'
                    }`}>
                    {method === 'whatsapp' ? <MessageSquare className="h-4 w-4" /> : method === 'sms' ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                    {canSend
                      ? method === 'whatsapp' ? 'Openen in WhatsApp'
                      : method === 'sms'       ? 'Openen in SMS-app'
                      :                          'Openen in e-mail'
                      : 'Geen contactgegevens beschikbaar'}
                  </button>
                  {!canSend && (
                    <p className="text-xs text-ink-4 text-center">
                      Dit bericht heeft geen {method === 'email' ? 'e-mailadres' : 'telefoonnummer'}.
                      Gebruik "Overslaan" om door te gaan.
                    </p>
                  )}
                </div>
              </div>

              {/* Navigatieknoppen */}
              <div className="px-5 pb-8 pt-4 flex gap-3 bg-sand flex-shrink-0 max-w-2xl mx-auto w-full">
                <button onClick={() => setBulkPhase('idle')}
                  className="flex-1 rounded-2xl border-2 border-sand-300 bg-white px-5 py-4 text-sm font-medium text-ink-3 hover:border-sage-300 transition-colors">
                  Stoppen
                </button>
                <button onClick={bulkNext}
                  className="flex-1 rounded-2xl bg-moss text-white px-5 py-4 text-sm font-semibold hover:bg-moss-light transition-colors">
                  {isLast ? 'Afronden ✓' : 'Volgend bericht →'}
                </button>
              </div>
            </div>
          )
        })()}

        {/* Berichtenlijst - V48: visueel gegroepeerd in persoonlijk + meldingen */}
        {(() => {
          const persoonlijk = messages.filter(m => !isPraktisch(m.recipientLabel))
          const praktisch   = messages.filter(m =>  isPraktisch(m.recipientLabel))

          function MessageCard({ m }: { m: typeof messages[0] }) {
            return (
              <Card key={m.id} padding="md" hoverable className="transition-all">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {m.status === 'READY'
                      ? <CheckCircle2 className="h-4 w-4 text-sage-600" />
                      : <Clock className="h-4 w-4 text-ink-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-ink">{m.recipientLabel}</p>
                      <StatusBadge status={m.status} />
                    </div>
                    {m.content && (
                      <p className="text-xs text-ink-4 mt-0.5 line-clamp-1 break-words">{truncate(m.content, 80)}</p>
                    )}
                    <p className="text-xs text-ink-4 mt-1">{formatDateNL(m.updatedAt)}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => openPreview(m)}
                      className="flex h-11 w-11 items-center justify-center rounded-xl text-ink-4 hover:bg-sage-50 hover:text-sage-600 transition-all active:scale-95"
                      title="Bekijk voorbeeld" aria-label="Bekijk voorbeeld"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button onClick={() => openEdit(m)}
                      className="flex h-11 w-11 items-center justify-center rounded-xl text-ink-4 hover:bg-blue-50 hover:text-blue-600 transition-all active:scale-95"
                      title="Bewerken" aria-label="Bewerken">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(m.id)}
                      className="flex h-11 w-11 items-center justify-center rounded-xl text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-all active:scale-95"
                      title="Verwijderen" aria-label="Verwijderen">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-sand-200">
                  <SendActions message={m} />
                </div>
              </Card>
            )
          }

          return (
            <div className="flex flex-col gap-4">
              {/* ❤️ Persoonlijke berichten */}
              {persoonlijk.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-rose-500 flex-shrink-0" />
                    <p className="text-xs font-semibold text-ink-3 uppercase tracking-wider">Persoonlijke berichten</p>
                  </div>
                  {persoonlijk.map(m => <MessageCard key={m.id} m={m} />)}
                </div>
              )}

              {/* 📋 Meldingen na overlijden */}
              {praktisch.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-ink-4 flex-shrink-0" />
                    <p className="text-xs font-semibold text-ink-3 uppercase tracking-wider">Meldingen na overlijden</p>
                  </div>
                  {praktisch.map(m => <MessageCard key={m.id} m={m} />)}
                </div>
              )}

              {/* Alles in één als geen groepering van toepassing is */}
              {persoonlijk.length === 0 && praktisch.length === 0 && messages.length > 0 && (
                <div className="flex flex-col gap-2">
                  {messages.map(m => <MessageCard key={m.id} m={m} />)}
                </div>
              )}
            </div>
          )
        })()}

        {!showForm && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={openNew} icon={<Plus className="h-4 w-4" />} className="flex-1">
              Bericht toevoegen
            </Button>
            {messages.filter(m => m.status === 'READY').length > 0 && (
              <Button onClick={startBulkSend} icon={<Send className="h-4 w-4" />}>
                Alles verzenden ({messages.filter(m => m.status === 'READY').length})
              </Button>
            )}
          </div>
        )}



        {messages.length > 0 && (
          <ClosingGuidance title="Uw berichten zijn voorbereid">
            Uw vertrouwenspersoon kan deze later delen volgens de door u vastgelegde wensen. WilsApp verstuurt nooit automatisch berichten.
          </ClosingGuidance>
        )}

        {/* V49: kruislink naar Persoonlijk afscheid */}
        <div className="rounded-2xl bg-sand border border-sand-200 px-4 py-4">
          <p className="text-sm text-ink-2 leading-relaxed mb-2.5">
            Uw persoonlijke brieven, rouwkaart en herinneringspagina beheert u in Persoonlijk afscheid.
          </p>
          <button type="button" onClick={() => navigate(ROUTES.WISHES_FAREWELL)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 bg-white px-4 py-2 text-sm font-medium text-ink-2 hover:bg-sand-50 transition-colors">
            Open Persoonlijk afscheid
          </button>
        </div>

            <BackButton variant="bottom" primaryLabel="Terug naar dashboard" />
    </ModuleShell>
    </div>
  )
}
